import './AccordionGeneric.css';
import React from 'react';
import { get } from '../../utils';
import { RichText, Text } from '@sitecore-jss/sitecore-jss-react';
import { useSitecoreContext } from '../../utils/SitecoreContextReactContext';
import ComponentsWrapper from '../atoms/ComponentsWrapper';
import IconPlus from '../atoms/Icons/IconPlus';
import IconMinus from '../atoms/Icons/IconMinus';
import IconChevronRight from '../atoms/Icons/IconChevronRight';
import IconChevronLeft from '../atoms/Icons/IconChevronLeft';
import { tealiumMergeClickData, tealiumClickFunc } from '../../utils/tealium';
import { convertToAnalyticObject } from '../../utils/analytics';
import { ReactComponent as ChevronDown } from '../../resources/icons/IconChevronDown.svg';
import { hexToRgb } from '../../utils/hexToRgb';
export const AccordionGeneric = _ref => {
  let {
    fields,
    params
  } = _ref;
  const sitecoreContext = useSitecoreContext();
  const rtl = Boolean(sitecoreContext && sitecoreContext.rtl);
  const pageEditing = Boolean(sitecoreContext && sitecoreContext.pageEditing);
  const isDetSite = get(params, 'RenderingStyle') === 'DDA';
  const hasTitle = Boolean(get(fields, 'data.item.title.jss.value'));
  const description = get(fields, 'data.item.description.jss.value', '');
  const isFirstExpanded = Boolean(get(fields, 'data.item.isFirstExpanded.jss.value'));
  const hasItems = Boolean(get(fields, 'data.item.children.length'));
  const items = hasItems ? fields.data.item.children : [];
  const filteredItems = items.filter(i => get(i, 'title.jss.value') && get(i, 'children.length'));
  const defaultStartItemId = items && items.length > 0 ? items[0].id : '0';
  const [activeItemId, setActiveItemId] = React.useState(defaultStartItemId);
  const [activeSubItemsIds, setActiveSubItemsIds] = React.useState([]);
  const activeItem = filteredItems.find(i => i.id === activeItemId);
  let eventsAnalytics = get(fields, 'data.eventsAnalytics') || [];
  if (eventsAnalytics.length) {
    eventsAnalytics = convertToAnalyticObject(eventsAnalytics);
  }
  const primaryPageThemeColor = get(sitecoreContext, 'themesOfPage.primary') || "#035E88";
  const variables = {
    '--primaryColorWithOpacityBackground': hexToRgb(primaryPageThemeColor).concat(", 0.1")
  };
  React.useEffect(() => {
    if (!filteredItems.length) return;
    setActiveItemId(filteredItems[0].id);
    if (isFirstExpanded && activeItem) {
      const [subItem] = activeItem.children;
      setActiveSubItemsIds([subItem.id]);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAccordionItemClick = React.useCallback((e, id) => {
    e.preventDefault();
    if (id !== activeItemId) {
      setActiveSubItemsIds([]);
      setActiveItemId(id);
    } else {
      //close opened faq only on mobile
      if (window.matchMedia('(max-width: 991px)').matches) setActiveItemId('');
    }
  }, [activeItemId]);
  const handleAccordionSubItemClick = React.useCallback((e, id, isActive) => {
    e.preventDefault();
    const nextActiveItemsIds = isActive ? activeSubItemsIds.filter(i => i !== id) : [...activeSubItemsIds, id];
    setActiveSubItemsIds(nextActiveItemsIds);
  }, [activeSubItemsIds]);
  if (!fields) {
    console.info('AccordionGeneric component render skipped. No fields provided');
    if (pageEditing) {
      return /*#__PURE__*/React.createElement("div", {
        className: "no-data"
      }, "[AccordionGeneric component is empty: please provide data source]");
    }
    return null;
  }
  const renderLeftPanel = () => {
    return /*#__PURE__*/React.createElement("div", {
      className: "desktop-only accordion-left-panel"
    }, filteredItems.map(item => {
      return /*#__PURE__*/React.createElement("h4", {
        key: item.id,
        className: `h4 accordion__heading ${item.id === activeItemId ? 'accordion__heading--active' : ''}`,
        "data-cy": "accordion-generic-category-title",
        onClick: e => {
          handleAccordionItemClick(e, item.id);
          tealiumClickFunc(tealiumMergeClickData(eventsAnalytics['categoryAnalytics'], params), `${item.name}`);
        }
      }, /*#__PURE__*/React.createElement(Text, {
        tag: "span",
        className: "accordion__anchor",
        field: item.title.jss
      }), /*#__PURE__*/React.createElement("span", {
        className: "accordion__icon-desk"
      }, rtl ? /*#__PURE__*/React.createElement(IconChevronLeft, null) : /*#__PURE__*/React.createElement(IconChevronRight, null)));
    }));
  };
  return /*#__PURE__*/React.createElement(StyledWrapper, {
    rtl: rtl,
    anchorUrlParameter: params['Anchor Url Parameter'],
    style: variables,
    isDetSite: isDetSite
  }, /*#__PURE__*/React.createElement("script", {
    type: "application/ld+json",
    dangerouslySetInnerHTML: {
      __html: `
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [

            ${items.map(item => {
        return get(item, 'children', []).map(faq => {
          return `{
                  "@type": "Question",
                  "name": ${JSON.stringify(get(faq, 'title.jss.value'))},
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": ${JSON.stringify(get(faq, 'description.jss.value'))}
                  }
                }`;
        });
      })}]
          }`
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "section-generic",
    "data-cy": "accordion-generic"
  }, /*#__PURE__*/React.createElement("div", {
    className: "titleDescriptionWrapper"
  }, hasTitle && /*#__PURE__*/React.createElement(Text, {
    tag: "div",
    className: isDetSite ? "step-title" : "accordion-main-heading",
    field: fields.data.item.title.jss,
    "data-cy": "accordion-generic-title"
  }), description ? /*#__PURE__*/React.createElement(RichText, {
    className: "accordionDescription",
    tag: "div",
    field: {
      value: description
    }
  }) : null), /*#__PURE__*/React.createElement("div", {
    className: "accordion accordion--generic"
  }, /*#__PURE__*/React.createElement("div", {
    className: "row accordion__row"
  }, renderLeftPanel(), /*#__PURE__*/React.createElement("div", {
    className: "accordion-right-panel"
  }, filteredItems.map(item => {
    const isActive = item.id === activeItemId;
    const activeParentItem = filteredItems.find(item => item.id === activeItemId);
    return /*#__PURE__*/React.createElement("div", {
      key: item.id,
      className: `accordion__list ${isActive ? 'accordion__list--active' : ''}`
    }, /*#__PURE__*/React.createElement("p", {
      className: "accordion__heading body-medium mobile-only",
      onClick: e => {
        handleAccordionItemClick(e, item.id);
        tealiumClickFunc(tealiumMergeClickData(eventsAnalytics['questionAnalytics'], params), `${item.name}: ${isActive ? 'Collapse' : 'Expand'}`);
      }
    }, /*#__PURE__*/React.createElement(Text, {
      tag: "span",
      field: item.title.jss
    }), isDetSite && /*#__PURE__*/React.createElement("span", {
      className: "accordion__icon"
    }, /*#__PURE__*/React.createElement(IconChevronLeft, null)), !isDetSite && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
      className: "accordion__icon"
    }, /*#__PURE__*/React.createElement(ChevronDown, null)), /*#__PURE__*/React.createElement("span", {
      className: "accordion__icon-desk"
    }))), isActive && /*#__PURE__*/React.createElement("div", {
      className: "accordion__content"
    }, /*#__PURE__*/React.createElement("div", {
      className: "cta-group"
    }, activeItem.description.jss.value && /*#__PURE__*/React.createElement(Text, {
      tag: "p",
      className: "widget-description",
      field: activeItem.description.jss
    }), activeItem.children.map(subItem => {
      const isSubItemActive = activeSubItemsIds.includes(subItem.id);
      const shouldBeRendered = subItem.title.jss.value || subItem.description.jss.value ? true : false;
      const iconSvgCode = get(subItem, "iconSvgCode.jss.value");
      if (!shouldBeRendered) {
        return null;
      }
      return /*#__PURE__*/React.createElement("div", {
        key: subItem.id,
        className: `cta-overlay__link ${isSubItemActive ? "cta-overlay__link--active" : ""}`
      }, /*#__PURE__*/React.createElement("div", {
        className: "cta-overlay__anchor-wrapper",
        onClick: e => {
          tealiumClickFunc(tealiumMergeClickData(eventsAnalytics['categoryQuestionAnalytics'], params), `${activeParentItem ? activeParentItem.name : ''}: ${subItem.name}: ${isSubItemActive ? 'Collapse' : 'Expand'}`);
          handleAccordionSubItemClick(e, subItem.id, isSubItemActive);
        }
      }, iconSvgCode && /*#__PURE__*/React.createElement("div", {
        dangerouslySetInnerHTML: {
          __html: iconSvgCode
        },
        className: "title__icon"
      }), /*#__PURE__*/React.createElement(Text, {
        tag: "span",
        className: "accordion__anchor cta-overlay__anchor",
        field: subItem.title.jss,
        "data-cy": "accordion-generic-anchor"
      }), /*#__PURE__*/React.createElement("span", {
        className: "accordion__icon-desktop"
      }, isDetSite ? /*#__PURE__*/React.createElement(IconChevronLeft, null) : isSubItemActive ? /*#__PURE__*/React.createElement(IconMinus, null) : /*#__PURE__*/React.createElement(IconPlus, null))), (isSubItemActive || pageEditing) && /*#__PURE__*/React.createElement("div", {
        className: "accordion-overlay",
        onClick: e => e.stopPropagation()
      }, /*#__PURE__*/React.createElement("div", {
        className: "accordion-overlay__content",
        "data-cy": "accordion-generic-item-content"
      }, /*#__PURE__*/React.createElement(RichText, {
        tag: "div",
        className: "body-small",
        field: subItem.description.jss
      }))));
    }))));
  }))))));
};
const StyledWrapper = _ref2 => {
  let {
    children,
    rtl,
    isDetSite,
    style,
    anchorUrlParameter
  } = _ref2;
  const commonStyles = isDetSite ? "c1g6k3ry" : "ct55cmv";
  const ltrStyles = isDetSite ? "lgk56uj" : "l1gfi1wv";
  const rtlStyles = isDetSite ? "r1g9z4kn" : "rgkqhkh";
  return /*#__PURE__*/React.createElement(ComponentsWrapper, {
    anchorUrlParameter: anchorUrlParameter,
    style: style,
    className: `${commonStyles} ${rtl ? rtlStyles : ltrStyles}`
  }, children);
};
export default AccordionGeneric;